import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SectionConfig } from "./use-admin-templates";

export type SectionSource = "body_region" | "modality";

export interface CombinedSectionItem extends SectionConfig {
  source: SectionSource;
}

interface UseCombinedSectionPoolResult {
  sections: CombinedSectionItem[];
  loading: boolean;
  fetch: (modality: string, bodyRegion: string, examDisplayName?: string) => Promise<void>;
  search: (query: string) => CombinedSectionItem[];
}

/**
 * Convert a section_token to a human-readable title
 * e.g., "left_circumflex" -> "Left Circumflex", "lad" -> "LAD"
 */
function tokenToTitle(token: string): string {
  // Handle common abbreviations that should stay uppercase
  const uppercaseTokens = ["lad", "lca", "lcx", "rca", "pda"];
  if (uppercaseTokens.includes(token.toLowerCase())) {
    return token.toUpperCase();
  }

  // Convert snake_case to Title Case
  return token
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

/**
 * Map exam_family_registry body_region values to their body_region_structure_pool equivalents.
 *
 * exam_family_registry uses values like: HEAD_BRAIN, HEAD, BRAIN, LUMBAR_SPINE, CHEST
 * body_region_structure_pool uses values like: HEAD_BRAIN, VASC_CORONARY, SPINE_LUMBAR
 */
function getBodyRegionPatterns(bodyRegion: string, examDisplayName?: string): string[] {
  const upper = bodyRegion.toUpperCase();
  const examName = (examDisplayName || "").toUpperCase();

  // Check if this is a cardiac/coronary exam based on display name
  // Cardiac exams often have body_region = CHEST but need VASC_CORONARY sections
  const isCoronaryExam =
    examName.includes("CORONARY") ||
    examName.includes("CARDIAC") ||
    examName.includes("CALCIUM SCORE") ||
    examName.includes("HEART");

  if (isCoronaryExam) {
    return ["VASC_CORONARY", "CARDIAC_CHAMBERS", "CARDIAC_VALVES", "CARDIAC_PERICARDIUM"];
  }

  // Map common exam body regions to their pool equivalents
  const mappings: Record<string, string[]> = {
    // Head/Brain exams
    "HEAD_BRAIN": ["HEAD_BRAIN"],
    "HEAD": ["HEAD_BRAIN", "HEAD_ORBITS", "HEAD_SINUSES", "HEAD_IAC", "HEAD_FACE"],
    "HEAD_NECK": ["HEAD_BRAIN", "NECK_SOFT_TISSUE", "VASC_CAROTID"],
    "BRAIN": ["HEAD_BRAIN"],
    "ORBITS": ["HEAD_ORBITS"],
    "SINUSES": ["HEAD_SINUSES"],
    "IAC": ["HEAD_IAC"],
    "FACE": ["HEAD_FACE", "HEAD_SINUSES"],

    // Spine exams (exam_family uses LUMBAR_SPINE, CERVICAL_SPINE, etc.)
    "LUMBAR_SPINE": ["SPINE_LUMBAR"],
    "CERVICAL_SPINE": ["SPINE_CERVICAL"],
    "THORACIC_SPINE": ["SPINE_THORACIC"],
    "SPINE": ["SPINE_CERVICAL", "SPINE_THORACIC", "SPINE_LUMBAR", "SPINE_WHOLE"],
    "SACRUM": ["SPINE_SACRUM"],

    // Chest exams (for pulmonary, not cardiac)
    "CHEST": ["CHEST_AIRWAYS", "CHEST_HRCT", "VASC_PULMONARY_ART"],

    // Abdomen/Pelvis
    "ABDOMEN": ["ABDOMEN_LIVER", "ABDOMEN_KIDNEY", "ABDOMEN_PANCREAS", "ABDOMEN_SPLEEN", "ABDOMEN_GI_UPPER", "ABDOMEN_GI_LOWER", "ABDOMEN_BILIARY"],
    "ABDOMEN_PELVIS": ["ABDOMEN_LIVER", "ABDOMEN_KIDNEY", "ABDOMEN_PANCREAS", "ABDOMEN_SPLEEN", "ABDOMEN_GI_UPPER", "ABDOMEN_GI_LOWER", "ABDOMEN_RETROPERITONEUM"],
    "PELVIS": ["ABDOMEN_GI_LOWER", "ABDOMEN_RETROPERITONEUM"],
    "CHEST_ABDOMEN": ["CHEST_AIRWAYS", "ABDOMEN_LIVER", "ABDOMEN_SPLEEN", "ABDOMEN_KIDNEY"],
    "CHEST_ABDOMEN_PELVIS": ["CHEST_AIRWAYS", "ABDOMEN_LIVER", "ABDOMEN_SPLEEN", "ABDOMEN_KIDNEY", "ABDOMEN_GI_LOWER"],
    "CHEST_ABD": ["CHEST_AIRWAYS", "ABDOMEN_LIVER", "ABDOMEN_SPLEEN"],

    // Vascular
    "AORTA": ["VASC_AORTA_THORACIC", "VASC_AORTA_ABDOMINAL"],
    "CAROTID": ["VASC_CAROTID"],
    "RUNOFF": ["VASC_LOWER_EXT_ART", "VASC_ILIAC"],

    // Neck
    "NECK": ["NECK_SOFT_TISSUE", "NECK_THYROID", "NECK_LARYNX"],

    // Breast
    "BREAST": ["BREAST_MAMMOGRAPHY", "BREAST_MRI"],

    // Extremities
    "SHOULDER": [],
    "FOOT": [],
  };

  // Check for direct mapping
  if (mappings[upper]) {
    return mappings[upper];
  }

  // Try partial match - if the exam body region contains a known key
  for (const [key, patterns] of Object.entries(mappings)) {
    if (upper.includes(key) || key.includes(upper)) {
      return patterns;
    }
  }

  // Fallback: try the body region as-is and with common prefixes
  return [upper, `HEAD_${upper}`, `CHEST_${upper}`, `ABDOMEN_${upper}`, `SPINE_${upper}`, `VASC_${upper}`, `CARDIAC_${upper}`];
}

/**
 * Hook that combines body region sections (specific) with modality sections (generic)
 * to provide a unified section pool for template configuration.
 *
 * Body region sections are specific (e.g., LAD, LCX for cardiac)
 * Modality sections are generic (e.g., Findings, Impression for CT)
 */
export function useCombinedSectionPool(): UseCombinedSectionPoolResult {
  const [sections, setSections] = useState<CombinedSectionItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (modality: string, bodyRegion: string, examDisplayName?: string) => {
    setLoading(true);
    try {
      // 1. Get body region sections (specific) from body_region_structure_pool
      // Map the simple body region to its detailed equivalents
      // Pass exam display name to detect coronary/cardiac exams
      const bodyRegionPatterns = getBodyRegionPatterns(bodyRegion, examDisplayName);

      // Query for all matching body regions
      const { data: bodyRegionData, error: bodyRegionError } = await supabase
        .from("body_region_structure_pool")
        .select("section_token")
        .in("body_region", bodyRegionPatterns)
        .eq("is_active", true);

      if (bodyRegionError) {
        console.warn("Failed to fetch body region sections:", bodyRegionError);
      }

      // Dedupe section_tokens and build CombinedSectionItem array
      const bodyRegionTokens = [
        ...new Set((bodyRegionData || []).map((d) => d.section_token)),
      ];
      const bodyRegionSections: CombinedSectionItem[] = bodyRegionTokens.map(
        (token, idx) => ({
          section_token: token,
          section_title: tokenToTitle(token),
          order_index: idx * 10,
          source: "body_region" as const,
        })
      );

      // 2. Get modality sections (generic) from modality_section_pool
      const { data: modalityData, error: modalityError } = await supabase
        .from("modality_section_pool")
        .select("section_token, section_title, order_index")
        .eq("modality", modality)
        .eq("is_active", true)
        .order("order_index");

      if (modalityError) {
        console.warn("Failed to fetch modality sections:", modalityError);
      }

      const modalitySections: CombinedSectionItem[] = (modalityData || []).map(
        (s) => ({
          section_token: s.section_token,
          section_title: s.section_title,
          order_index: s.order_index,
          source: "modality" as const,
        })
      );

      // 3. Combine: body region first, then modality
      // Dedupe by section_token (body region wins if duplicate exists)
      const seen = new Set<string>();
      const combined: CombinedSectionItem[] = [];

      // Add body region sections first
      for (const section of bodyRegionSections) {
        if (!seen.has(section.section_token)) {
          seen.add(section.section_token);
          combined.push(section);
        }
      }

      // Then add modality sections (only if not already seen)
      for (const section of modalitySections) {
        if (!seen.has(section.section_token)) {
          seen.add(section.section_token);
          combined.push(section);
        }
      }

      setSections(combined);
    } catch (err) {
      console.error("Failed to fetch combined section pool:", err);
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Filter sections by search query
   * Matches against section_title and section_token
   */
  const search = useCallback(
    (query: string): CombinedSectionItem[] => {
      if (!query.trim()) return sections;
      const lowerQuery = query.toLowerCase();
      return sections.filter(
        (s) =>
          s.section_title.toLowerCase().includes(lowerQuery) ||
          s.section_token.toLowerCase().includes(lowerQuery)
      );
    },
    [sections]
  );

  return {
    sections,
    loading,
    fetch,
    search,
  };
}
